import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { getAllBlogPosts, deleteBlogPost } from '../../firebase/models/blogService';

const Container = styled.div`
  padding: var(--spacing-xl);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
`;

const Title = styled.h1`
  margin: 0;
`;

const Button = styled(Link)`
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--accent-blue);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background-color: var(--dark-blue);
  }
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: var(--spacing-md);
  border-bottom: 2px solid #eee;
`;

const Td = styled.td`
  padding: var(--spacing-md);
  border-bottom: 1px solid #eee;
`;

const ActionButton = styled.button`
  padding: var(--spacing-sm);
  margin-right: var(--spacing-sm);
  background-color: ${props => props.delete ? '#ff4d4d' : '#f0f0f0'};
  color: ${props => props.delete ? 'white' : 'inherit'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.delete ? '#e60000' : '#e0e0e0'};
  }
`;

const ActionLink = styled(Link)`
  padding: var(--spacing-sm);
  margin-right: var(--spacing-sm);
  background-color: #f0f0f0;
  color: inherit;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

const Message = styled.p`
  padding: var(--spacing-md);
  background-color: ${props => props.type === 'error' ? '#ffeeee' : '#eeffee'};
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

const EmptyState = styled.div`
  padding: var(--spacing-xl);
  text-align: center;
  color: #888;
`;

const formatDate = (timestamp) => {
  if (!timestamp) return 'Unknown date';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const truncate = (text, length = 50) => {
  if (!text) return '';
  return text.length > length ? text.substring(0, length) + '...' : text;
};

const PostList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const postsData = await getAllBlogPosts();
      setPosts(postsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load blog posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPosts();
  }, []);
  
  const handleDelete = async (postId, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await deleteBlogPost(postId);
        setSuccess(`Post "${title}" was deleted successfully.`);
        fetchPosts(); // Refresh the list
      } catch (err) {
        console.error('Error deleting post:', err);
        setError('Failed to delete post. Please try again.');
      }
    }
  };
  
  return (
    <Container>
      <Header>
        <Title>Manage Blog Posts</Title>
        <Button to="/admin/posts/new">Create New Post</Button>
      </Header>
      
      {error && <Message type="error">{error}</Message>}
      {success && <Message>{success}</Message>}
      
      <Card>
        {loading ? (
          <p>Loading posts...</p>
        ) : posts.length === 0 ? (
          <EmptyState>
            <p>No posts found. Start by creating your first blog post.</p>
            <Button to="/admin/posts/new">Create New Post</Button>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Title</Th>
                <Th>Published Date</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {posts.map(post => (
                <tr key={post.id}>
                  <Td>{truncate(post.title, 40)}</Td>
                  <Td>{formatDate(post.createdAt)}</Td>
                  <Td>{post.published ? 'Published' : 'Draft'}</Td>
                  <Td>
                    <ActionLink to={`/admin/posts/edit/${post.id}`}>Edit</ActionLink>
                    <ActionLink to={`/blog/${post.slug}`} target="_blank">View</ActionLink>
                    <ActionButton 
                      delete
                      onClick={() => handleDelete(post.id, post.title)}
                    >
                      Delete
                    </ActionButton>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Container>
  );
};

export default PostList; 